'use client'

import React from 'react'

import { GitCompareArrows, Search, Trophy } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

import { ExportDropdown } from './export'

interface ActionButtonProps {
  icon: React.ElementType
  label: string
  disabled?: boolean
  onClick?: () => void
}

function ActionButton({
  icon: Icon,
  label,
  disabled = true,
  onClick,
}: ActionButtonProps) {
  const button = (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={twMerge(
        'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md',
        'text-xs font-medium transition-colors',
        'text-primary-600 hover:bg-primary-100 dark:text-primary-400 dark:hover:bg-primary-800',
        disabled &&
          'opacity-50 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent',
      )}
    >
      <Icon className="h-4 w-4" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  )

  if (disabled) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="bottom">Coming soon</TooltipContent>
      </Tooltip>
    )
  }

  return button
}

/**
 * RecipientDetail.QuickActions
 *
 * A horizontal bar with quick action buttons for competitive intelligence workflows.
 * Export button is now functional with PDF export capability.
 */
export function RecipientQuickActions() {
  return (
    <div className="w-full border-b border-primary-200 bg-white dark:border-primary-800 dark:bg-primary-950">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="flex items-center gap-1 py-1.5 overflow-x-auto">
          <ActionButton icon={Search} label="Find Similar" />
          <ActionButton icon={GitCompareArrows} label="Compare" />
          <ActionButton icon={Trophy} label="Won Opportunities" />
          <ExportDropdown />
        </div>
      </div>
    </div>
  )
}
